/**
 * Rate Limit Service
 * 
 * Centralized rate limiting for ALL LLM calls across the application.
 * Prevents runaway query loops and excessive API costs.
 * 
 * Default limits:
 * - Per query type: 15 queries per 60 seconds
 * - Global: 30 queries per 60 seconds across all types
 */

class RateLimitService {
  constructor() {
    // Rate limiting configuration
    this.config = {
      maxQueriesPerWindow: 15,    // Max queries per query type per window
      windowMs: 60000,            // 60 second window
      globalMaxPerMinute: 30,     // Global max across all query types
      // Optional per-key overrides (e.g. symptom checker can trigger many calls: pause + auto-refresh + retries)
      limitsByKey: {
        'symptom_checker/ddx': 30,
      },
    };
    
    // Query timestamps for rate limiting: Map<queryId, timestamp[]>
    this.queryTimestamps = new Map();
    this.globalQueryTimestamps = [];
    
    // Rate limit exceeded callbacks - for UI feedback
    this.rateLimitExceededCallbacks = [];
    
    // Exempted query types (e.g., warmup pings)
    this.exemptedQueries = new Set(['warmup', 'function_warmup', 'ping']);
  }

  /**
   * Configure rate limits
   * @param {Object} config - Rate limit configuration
   */
  configure(config) {
    this.config = { ...this.config, ...config };
  }

  /**
   * Add an exempted query type
   * @param {string} queryId - Query type to exempt from rate limiting
   */
  addExemption(queryId) {
    this.exemptedQueries.add(queryId);
  }

  /**
   * Register a callback to be notified when rate limit is exceeded
   * @param {Function} callback - Called with (queryId, remainingMs, reason)
   * @returns {Function} Unsubscribe function
   */
  onRateLimitExceeded(callback) {
    this.rateLimitExceededCallbacks.push(callback);
    return () => {
      this.rateLimitExceededCallbacks = this.rateLimitExceededCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Check if a query is rate limited
   * @param {string} queryId - The query type ID
   * @returns {{ limited: boolean, remainingMs: number, reason: string }}
   */
  checkRateLimit(queryId) {
    // Skip rate limiting for exempted queries
    if (this.exemptedQueries.has(queryId)) {
      return { limited: false, remainingMs: 0, reason: '' };
    }

    const now = Date.now();
    const windowStart = now - this.config.windowMs;
    
    // Clean up old timestamps
    this.globalQueryTimestamps = this.globalQueryTimestamps.filter(ts => ts > windowStart);
    
    // Check global rate limit first
    if (this.globalQueryTimestamps.length >= this.config.globalMaxPerMinute) {
      const oldestInWindow = Math.min(...this.globalQueryTimestamps);
      const remainingMs = (oldestInWindow + this.config.windowMs) - now;
      return {
        limited: true,
        remainingMs,
        reason: `Global rate limit exceeded (${this.config.globalMaxPerMinute} queries/min)`,
      };
    }
    
    // Check per-query-type rate limit (use per-key override if set)
    const limitForQuery = (this.config.limitsByKey && this.config.limitsByKey[queryId]) ?? this.config.maxQueriesPerWindow;
    let timestamps = this.queryTimestamps.get(queryId) || [];
    timestamps = timestamps.filter(ts => ts > windowStart);
    this.queryTimestamps.set(queryId, timestamps);

    if (timestamps.length >= limitForQuery) {
      const oldestInWindow = Math.min(...timestamps);
      const remainingMs = (oldestInWindow + this.config.windowMs) - now;
      return {
        limited: true,
        remainingMs,
        reason: `Rate limit exceeded for ${queryId} (${limitForQuery} queries/min)`,
      };
    }
    
    return { limited: false, remainingMs: 0, reason: '' };
  }

  /**
   * Record a query execution for rate limiting
   * @param {string} queryId - The query type ID
   */
  recordQuery(queryId) {
    // Skip recording for exempted queries
    if (this.exemptedQueries.has(queryId)) {
      return;
    }

    const now = Date.now();
    
    // Record global timestamp
    this.globalQueryTimestamps.push(now);
    
    // Record per-query-type timestamp
    const timestamps = this.queryTimestamps.get(queryId) || [];
    timestamps.push(now);
    this.queryTimestamps.set(queryId, timestamps);
  }

  /**
   * Check rate limit and throw if exceeded
   * @param {string} queryId - The query type ID
   * @throws {Error} If rate limit is exceeded
   */
  enforceRateLimit(queryId) {
    const rateLimitCheck = this.checkRateLimit(queryId);
    
    if (rateLimitCheck.limited) {
      console.warn(`[RateLimitService] RATE LIMITED: ${rateLimitCheck.reason}. Wait ${Math.ceil(rateLimitCheck.remainingMs / 1000)}s`);
      
      // Notify callbacks
      this.rateLimitExceededCallbacks.forEach(cb => {
        try {
          cb(queryId, rateLimitCheck.remainingMs, rateLimitCheck.reason);
        } catch (e) {
          console.error('[RateLimitService] Callback error:', e);
        }
      });
      
      throw new Error(`Rate limit exceeded: ${rateLimitCheck.reason}. Please wait ${Math.ceil(rateLimitCheck.remainingMs / 1000)} seconds.`);
    }
  }

  /**
   * Get current rate limit status for monitoring
   * @returns {Object} Rate limit status
   */
  getStatus() {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;
    
    // Clean and count global queries
    this.globalQueryTimestamps = this.globalQueryTimestamps.filter(ts => ts > windowStart);
    
    const perQueryCounts = {};
    this.queryTimestamps.forEach((timestamps, queryId) => {
      const validTimestamps = timestamps.filter(ts => ts > windowStart);
      this.queryTimestamps.set(queryId, validTimestamps);
      perQueryCounts[queryId] = validTimestamps.length;
    });
    
    return {
      globalCount: this.globalQueryTimestamps.length,
      globalLimit: this.config.globalMaxPerMinute,
      globalRemaining: this.config.globalMaxPerMinute - this.globalQueryTimestamps.length,
      perQueryCounts,
      perQueryLimit: this.config.maxQueriesPerWindow,
      windowMs: this.config.windowMs,
    };
  }

  /**
   * Reset rate limit tracking (for testing/debugging)
   */
  reset() {
    this.queryTimestamps.clear();
    this.globalQueryTimestamps = [];
  }
}

// Export singleton instance
export const rateLimitService = new RateLimitService();
export default rateLimitService;
